import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEquipo } from '../context/EquipoContext';
import { useAuth } from '../context/AuthContext';
import { getEquipos } from '../services/api';

const ZONAS      = ['Todas las zonas', 'Machalí', 'Rancagua', 'Sewell'];
const ORDEN_OPTS = ['Compatibilidad', 'ELO más cercano', 'Reputación'];

function calcularCompatibilidad(miEquipo, rival) {
  let puntos = 0;
  if (miEquipo.zona === rival.zona) puntos += 40;
  const diffElo = Math.abs((miEquipo.elo || 1000) - (rival.elo || 1000));
  puntos += Math.max(0, 30 - Math.floor(diffElo / 10));
  puntos += 10;
  puntos += 5;
  return Math.min(100, puntos);
}

function getIniciales(nombre) {
  return nombre.split(' ').filter(p => p.length > 0).slice(0, 2).map(p => p[0].toUpperCase()).join('');
}

function nombreCorto(nombre) {
  const partes = nombre.split(' ');
  return partes.length > 2 ? partes.slice(0, 2).join(' ') : nombre;
}

export default function Matchmaking() {
  const navigate             = useNavigate();
  const { equipo, deporteActivo } = useEquipo();
  const { user }             = useAuth();

  const esFutbol = !deporteActivo || deporteActivo === 'futbol';

  const [rivalSel,     setRivalSel]     = useState(null);
  const [modalInvitar, setModalInvitar] = useState(false);
  const [nombreExt,    setNombreExt]    = useState('');
  const [celularExt,   setCelularExt]   = useState('');
  const [enviandoInv,  setEnviandoInv]  = useState(false);
  const [invExitosa,   setInvExitosa]   = useState(false);
  const [zona,         setZona]         = useState('Todas las zonas');
  const [orden,        setOrden]        = useState('Compatibilidad');
  const [desafioRival, setDesafioRival] = useState(null);
  const [rivales,      setRivales]      = useState([]);
  const [cargando,     setCargando]     = useState(true);

  useEffect(() => {
    setCargando(true);
    getEquipos()
      .then(data => {
        if (Array.isArray(data)) {
          const otros = data.filter(e =>
            e.id !== equipo?.id &&
            e.capitan_id !== user?.id &&
            // Filtrar por deporte activo
            (esFutbol
              ? (!e.deporte || e.deporte === 'futbol')
              : e.deporte === deporteActivo)
          );
          const adaptados = otros.map(e => ({
            ...e,
            iniciales:       getIniciales(e.nombre),
            colorPrincipal:  e.color_principal  || '#1A6BFF',
            colorSecundario: e.color_secundario || '#FF6B1A',
            slug:            e.url_nombre,
            modalidades:     e.modalidades || [],
            disponibilidad:  e.dias        || [],
            victorias:       e.victorias   || 0,
            // ELO según deporte
            elo: esFutbol
              ? (e.elo || 1000)
              : deporteActivo === 'padel'
              ? (e.elo_padel || 1000)
              : (e.elo_tenis || 1000),
            comentario: 'Perfil registrado en TuRival.',
            deltaElo: 0,
          }));
          setRivales(adaptados);
        }
      })
      .catch(() => setRivales([]))
      .finally(() => setCargando(false));
  }, [equipo, user, deporteActivo]);

  const miEquipo = useMemo(() => equipo || { elo: 1000, zona: '' }, [equipo]);

  const rivalesFiltrados = useMemo(() => {
    let lista = rivales.map(r => ({
      ...r,
      compatibilidad: calcularCompatibilidad(miEquipo, r),
    }));

    if (zona !== 'Todas las zonas') {
      lista = lista.filter(r => r.zona === zona);
    }

    if (orden === 'Compatibilidad') {
      lista.sort((a, b) => b.compatibilidad - a.compatibilidad);
    } else if (orden === 'ELO más cercano') {
      lista.sort((a, b) =>
        Math.abs((miEquipo.elo || 1000) - a.elo) -
        Math.abs((miEquipo.elo || 1000) - b.elo)
      );
    }

    return lista;
  }, [rivales, zona, orden, miEquipo]);

  const handleEnviarInvitacion = async () => {
    if (!nombreExt) return;
    setEnviandoInv(true);
    try {
      const API = process.env.REACT_APP_API_URL;
      const res = await fetch(`${API}/api/invitaciones/externa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo:            'referido',
          deporte:         deporteActivo || 'futbol',
          nombre:          nombreExt,
          celular:         celularExt || null,
          invitador_id:    equipo?.capitan_id,
          invitador_nombre: equipo?.capitan_nombre || user?.nombre,
          enviar_wsp:      false,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        // El capitán envía el WhatsApp desde su propio número (evita bloqueo de Meta).
        // Si escribió el celular → abre chat directo con ese número.
        // Si NO lo escribió → WhatsApp abre el selector de contactos para elegir a quién.
        const texto = data.texto_compartir || `Te invito a jugar en TuRival: ${data.link}`;
        const num   = String(celularExt || '').replace(/\D/g, '');
        const dst   = num ? (num.startsWith('56') ? num : `56${num}`) : '';
        const urlWsp = dst
          ? `https://wa.me/${dst}?text=${encodeURIComponent(texto)}`
          : `https://wa.me/?text=${encodeURIComponent(texto)}`;
        window.open(urlWsp, '_blank');
        setInvExitosa(true); setNombreExt(''); setCelularExt('');
      }
      else alert(data.error || 'Error al enviar invitación');
    } catch { alert('Error de conexión'); }
    finally { setEnviandoInv(false); }
  };

  const handleDesafiar = (rival) => {
    setDesafioRival(rival);
    navigate('/app/desafios', { state: { rival } });
  };

  if (desafioRival) return null;

  const deporteLabel = esFutbol ? 'Fútbol' : deporteActivo === 'padel' ? 'Pádel' : 'Tenis';

  return (
    <>
    <div style={styles.pagina}>
      <div style={styles.pageHeader}>
        <div style={styles.pageTitle}>
          Buscar <span style={styles.pageTitleSpan}>rival</span>
          <span style={styles.deporteBadge}>{deporteLabel}</span>
        </div>
        <div style={styles.pageSub}>
          {cargando
            ? 'Cargando...'
            : `${rivalesFiltrados.length} ${esFutbol ? 'equipos' : 'perfiles'} disponibles para desafiar`}
        </div>
      </div>

      <div style={styles.filtrosWrap}>
        <div style={styles.filtroGrupo}>
          <span style={styles.filtroLabel}>Zona</span>
          <select style={styles.select} value={zona} onChange={e => setZona(e.target.value)}>
            {ZONAS.map(z => <option key={z}>{z}</option>)}
          </select>
        </div>
        <div style={styles.filtroGrupo}>
          <span style={styles.filtroLabel}>Ordenar</span>
          <select style={styles.select} value={orden} onChange={e => setOrden(e.target.value)}>
            {ORDEN_OPTS.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <div style={styles.contenido}>
        <div style={styles.tablaWrap}>
          <div style={styles.tablaHeader}>
            <div style={styles.th}>{esFutbol ? 'Equipo' : 'Jugador / Perfil'}</div>
            <div style={styles.th}>ELO</div>
            <div style={styles.th}>Compatibilidad</div>
            <div style={styles.th}>Zona</div>
            <div style={styles.th}></div>
          </div>
          <div style={styles.tablaBody}>
            {cargando ? (
              <div style={styles.cargando}>Cargando {esFutbol ? 'equipos' : 'perfiles'}...</div>
            ) : rivalesFiltrados.length === 0 ? (
              <div style={styles.vacio}>
                <div>No hay {esFutbol ? 'equipos' : 'perfiles de ' + deporteLabel} disponibles aún.</div>
                <div style={{ fontSize: 12, color: '#9BA5B7', marginTop: 6, marginBottom: 12 }}>
                  ¿Tu rival no está en TuRival? ¡Invítalo!
                </div>
                <button
                  onClick={() => { setModalInvitar(true); setInvExitosa(false); setNombreExt(''); setCelularExt(''); }}
                  style={{ background: '#FF6B1A', color: 'white', border: 'none', borderRadius: 10,
                    padding: '10px 20px', cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: 14, fontWeight: 800 }}
                >
                  ⚡ Invitar rival externo
                </button>
              </div>
            ) : (
              rivalesFiltrados.map((r, i) => {
                const top = i === 0;
                const sel = rivalSel?.id === r.id;
                return (
                  <div key={r.id} style={styles.fila(top, sel)} onClick={() => setRivalSel(r)}>
                    <div style={styles.rivalIdentidad}>
                      <div style={styles.crest(r.colorPrincipal, r.colorSecundario)}>
                        {r.iniciales}
                      </div>
                      <div>
                        <div style={styles.rivalNombre}>{r.nombre}</div>
                        <div style={styles.rivalMeta}>
                          {r.zona}
                          {r.modalidades?.length > 0 ? ' · ' + r.modalidades.slice(0,2).join(', ') : ''}
                        </div>
                        {r.capitan_nombre && (
                          <div style={{ fontSize: 10, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif", marginTop: 1 }}>
                            {esFutbol ? 'Cap' : 'Jugador'}: {r.capitan_nombre}
                          </div>
                        )}
                      </div>
                    </div>
                    <div><div style={styles.eloNum}>{r.elo}</div></div>
                    <div>
                      <div style={styles.compatBar}>
                        <div style={styles.compatFill(r.compatibilidad)} />
                      </div>
                      <div style={styles.compatPct(r.compatibilidad)}>{r.compatibilidad}% match</div>
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280', fontFamily: "'Barlow', sans-serif" }}>
                      {r.zona || '—'}
                    </div>
                    <div>
                      <button style={styles.btnDesafiar(top)} onClick={e => { e.stopPropagation(); handleDesafiar(r); }}>
                        {top ? 'Desafiar ⚡' : 'Desafiar'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div style={styles.panel}>
          {rivalSel && (
            <div>
              <div style={styles.panelTitle}>Perfil del rival</div>
              <div style={styles.perfilHeader}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 11, flexShrink: 0, background: `linear-gradient(135deg, ${rivalSel.colorPrincipal}, ${rivalSel.colorSecundario})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 800, color: 'white' }}>
                    {rivalSel.iniciales}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={styles.perfilNombre}>{rivalSel.nombre}</div>
                    <div style={styles.perfilMeta}>{rivalSel.zona}{rivalSel.modalidades?.length > 0 ? ' · ' + rivalSel.modalidades.join(', ') : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 28, fontWeight: 800, color: '#1A6BFF', lineHeight: 1 }}>{rivalSel.elo}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'Barlow', sans-serif" }}>ELO</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {[
                    { n: rivalSel.victorias || 0, l: 'Victorias', color: '#16A34A' },
                    { n: rivalSel.empates   || 0, l: 'Empates',   color: '#F59E0B' },
                    { n: (rivalSel.partidos||0)-(rivalSel.victorias||0)-(rivalSel.empates||0), l: 'Derrotas', color: '#EF4444' },
                    { n: rivalSel.partidos  || 0, l: 'Partidos',  color: 'white' },
                  ].map(({ n, l, color }) => (
                    <div key={l} style={styles.statBox}>
                      <div style={styles.statN(color)}>{n}</div>
                      <div style={styles.statL}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
              <button style={styles.btnDesafiarPanel} onClick={() => handleDesafiar(rivalSel)}>
                Desafiar a {nombreCorto(rivalSel.nombre)} ⚡
              </button>
              <button style={styles.btnVerPerfil} onClick={() => navigate(`/equipo/${rivalSel.slug}`)}>
                Ver perfil completo →
              </button>
            </div>
          )}

          <div>
            <div style={styles.panelTitle}>Top {deporteLabel}</div>
            {rivalesFiltrados.slice(0, 5).map((r, i) => (
              <div key={r.id} style={styles.rankItem(false)}>
                <div style={styles.rankNum(i + 1)}>{i + 1}</div>
                <div style={styles.rankCrest(r.colorPrincipal, r.colorSecundario)}>{r.iniciales}</div>
                <div style={styles.rankNombre(false)}>{r.nombre}</div>
                <div style={styles.rankElo(false)}>{r.elo}</div>
              </div>
            ))}
            {rivalesFiltrados.length === 0 && (
              <div style={{ fontSize: 12, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif", padding: '10px 0' }}>
                Sin {esFutbol ? 'equipos' : 'perfiles'} registrados aún
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Banner invitar rival externo — siempre visible */}
      <div style={{ background: '#0D1117', borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 15,
            fontWeight: 700, color: 'white', marginBottom: 2 }}>
            ¿Tu rival no está en TuRival?
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" }}>
            Invítalo por WhatsApp y desafíalo cuando se registre
          </div>
        </div>
        <button
          onClick={() => { setModalInvitar(true); setInvExitosa(false); setNombreExt(''); setCelularExt(''); }}
          style={{ background: '#FF6B1A', color: 'white', border: 'none', borderRadius: 10,
            padding: '10px 20px', cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          ⚡ Invitar rival
        </button>
      </div>
    </div>

      {/* Modal invitar rival externo */}
      {modalInvitar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 28,
            width: '100%', maxWidth: 400 }}>
            {invExitosa ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22,
                  fontWeight: 800, color: '#141922', marginBottom: 8 }}>
                  ¡Invitación enviada!
                </div>
                <div style={{ fontSize: 13, color: '#6B7280', fontFamily: "'Barlow', sans-serif",
                  marginBottom: 20 }}>
                  Se abrió WhatsApp con el mensaje listo para {nombreExt || 'tu rival'}. Solo aprieta enviar y le llegará el link para registrarse en TuRival.
                </div>
                <button onClick={() => setModalInvitar(false)}
                  style={{ background: '#FF6B1A', color: 'white', border: 'none', borderRadius: 12,
                    padding: '12px 28px', cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: 16, fontWeight: 800 }}>
                  Cerrar
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 20,
                    fontWeight: 800, color: '#141922' }}>
                    ⚡ Invitar rival a TuRival
                  </div>
                  <button onClick={() => setModalInvitar(false)}
                    style={{ background: '#F4F6FA', border: 'none', borderRadius: 8,
                      width: 32, height: 32, cursor: 'pointer', color: '#6B7280', fontSize: 14 }}>
                    ✕
                  </button>
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', fontFamily: "'Barlow', sans-serif",
                  marginBottom: 16, lineHeight: 1.5 }}>
                  Se abrirá tu WhatsApp con un mensaje listo para que se lo envíes e invitarlo a registrarse en TuRival.
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    fontFamily: "'Barlow', sans-serif", marginBottom: 6 }}>Nombre</div>
                  <input value={nombreExt} onChange={e => setNombreExt(e.target.value)}
                    placeholder="Nombre del rival"
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #E8ECF4',
                      borderRadius: 10, fontSize: 13, fontFamily: "'Barlow', sans-serif",
                      outline: 'none', boxSizing: 'border-box', color: '#141922' }} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    fontFamily: "'Barlow', sans-serif", marginBottom: 6 }}>Celular (WhatsApp) — opcional</div>
                  <input value={celularExt} onChange={e => setCelularExt(e.target.value)}
                    placeholder="Solo si no lo tienes en tus contactos"
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #E8ECF4',
                      borderRadius: 10, fontSize: 13, fontFamily: "'Barlow', sans-serif",
                      outline: 'none', boxSizing: 'border-box', color: '#141922' }} />
                </div>
                <button
                  onClick={handleEnviarInvitacion}
                  disabled={!nombreExt || enviandoInv}
                  style={{ width: '100%', padding: '13px', background: '#FF6B1A', border: 'none',
                    borderRadius: 12, cursor: !nombreExt ? 'not-allowed' : 'pointer',
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 800,
                    color: 'white', opacity: !nombreExt || enviandoInv ? 0.5 : 1 }}>
                  {enviandoInv ? 'Abriendo...' : '⚡ Abrir WhatsApp para invitar'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  pagina: { minHeight: '100vh', background: '#F4F6FA' },
  pageHeader: { background: '#0D1117', padding: '24px 32px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  pageTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 32, fontWeight: 800, color: 'white', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 12 },
  pageTitleSpan: { color: '#1A6BFF' },
  deporteBadge: { background: '#FF6B1A', color: 'white', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" },
  pageSub: { fontSize: 13, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" },
  filtrosWrap: { background: 'white', borderBottom: '1px solid #E8ECF4', padding: '14px 32px', display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' },
  filtroGrupo: { display: 'flex', alignItems: 'center', gap: 6 },
  filtroLabel: { fontSize: 11, fontWeight: 700, color: '#9BA5B7', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'Barlow', sans-serif", whiteSpace: 'nowrap' },
  select: { background: '#F4F6FA', border: '1px solid #E8ECF4', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#4A5568', cursor: 'pointer', fontFamily: "'Barlow', sans-serif", outline: 'none' },
  contenido: { display: 'grid', gridTemplateColumns: '1fr 320px', height: 'calc(100vh - 160px)' },
  tablaWrap: { overflowY: 'auto', padding: '20px 24px' },
  tablaHeader: { display: 'grid', gridTemplateColumns: '2fr 0.8fr 1fr 0.8fr 100px', padding: '10px 16px', background: '#F4F6FA', borderRadius: '12px 12px 0 0', border: '1px solid #E8ECF4', borderBottom: 'none' },
  th: { fontSize: 10, fontWeight: 700, color: '#9BA5B7', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'Barlow', sans-serif" },
  tablaBody: { background: 'white', border: '1px solid #E8ECF4', borderRadius: '0 0 12px 12px', overflow: 'hidden' },
  fila: (top, sel) => ({ display: 'grid', gridTemplateColumns: '2fr 0.8fr 1fr 0.8fr 100px', padding: '12px 16px', borderBottom: '1px solid #F4F6FA', alignItems: 'center', cursor: 'pointer', background: sel ? '#EFF5FF' : top ? '#FAFCFF' : 'white', borderLeft: `3px solid ${sel ? '#1A6BFF' : top ? '#FF6B1A' : 'transparent'}`, transition: 'background 0.12s' }),
  rivalIdentidad: { display: 'flex', alignItems: 'center', gap: 10 },
  crest: (c1, c2) => ({ width: 38, height: 38, borderRadius: 10, background: `linear-gradient(135deg, ${c1}, ${c2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 800, color: 'white', flexShrink: 0 }),
  rivalNombre: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 15, fontWeight: 700, color: '#141922', marginBottom: 1 },
  rivalMeta: { fontSize: 11, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif" },
  eloNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 20, fontWeight: 800, color: '#141922' },
  compatBar: { height: 5, borderRadius: 3, background: '#E8ECF4', overflow: 'hidden', marginBottom: 2 },
  compatFill: (pct) => ({ height: '100%', borderRadius: 3, width: `${pct}%`, background: pct >= 85 ? '#FF6B1A' : pct >= 65 ? '#1A6BFF' : '#9BA5B7' }),
  compatPct: (pct) => ({ fontSize: 10, fontWeight: 700, color: pct >= 85 ? '#FF6B1A' : pct >= 65 ? '#1A6BFF' : '#9BA5B7' }),
  btnDesafiar: (top) => ({ background: top ? '#FF6B1A' : '#1A6BFF', color: 'white', border: 'none', borderRadius: 8, padding: '7px 12px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }),
  vacio: { padding: '40px', textAlign: 'center', color: '#9BA5B7', fontSize: 13, fontFamily: "'Barlow', sans-serif" },
  cargando: { padding: '40px', textAlign: 'center', color: '#9BA5B7', fontSize: 13, fontFamily: "'Barlow', sans-serif" },
  panel: { borderLeft: '1px solid #E8ECF4', background: 'white', overflowY: 'auto', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 },
  panelTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 12, fontWeight: 700, color: '#9BA5B7', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
  perfilHeader: { background: '#0D1117', borderRadius: 12, padding: 14, marginBottom: 10 },
  perfilNombre: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 18, fontWeight: 800, color: 'white', marginBottom: 2 },
  perfilMeta: { fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif", marginBottom: 8 },
  statBox: { background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '8px', textAlign: 'center' },
  statN: (color) => ({ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 18, fontWeight: 800, color: color || 'white', marginBottom: 2 }),
  statL: { fontSize: 9, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: "'Barlow', sans-serif" },
  btnDesafiarPanel: { width: '100%', padding: '12px', background: '#FF6B1A', border: 'none', borderRadius: 12, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 800, color: 'white', marginBottom: 8 },
  btnVerPerfil: { width: '100%', padding: '10px', background: 'white', border: '1.5px solid #E8ECF4', borderRadius: 12, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, color: '#4A5568' },
  rankItem: (me) => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, marginBottom: 4, background: me ? 'rgba(26,107,255,0.08)' : 'transparent' }),
  rankNum: (i) => ({ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, color: i === 1 ? '#F59E0B' : i === 2 ? '#9BA5B7' : i === 3 ? '#CD7F32' : '#9BA5B7', minWidth: 18 }),
  rankCrest: (c1, c2) => ({ width: 26, height: 26, borderRadius: 6, background: `linear-gradient(135deg, ${c1}, ${c2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 9, fontWeight: 800, color: 'white' }),
  rankNombre: (me) => ({ flex: 1, fontSize: 12, fontFamily: "'Barlow', sans-serif", color: me ? '#1A6BFF' : '#141922', fontWeight: me ? 700 : 500 }),
  rankElo: (me) => ({ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, color: me ? '#1A6BFF' : '#9BA5B7' }),
};
